import { useMemo, useState } from "react";
import { CalendarRange, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { WeeklySlotsEditor } from "./WeeklySlotsEditor";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
const fmtShort = (d) => new Date(d + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });
const summary = (ws) => Object.entries(ws).filter(([, s]) => s.length).map(([k, s]) => `${DAYS[k]} ${[...s].sort()[0]}`).join(", ") || "nessun ricevimento";
const periodLabel = (p) => (p.end ? `${fmt(p.start)} → ${fmt(p.end)}` : `dal ${fmt(p.start)} in avanti`);

function ConfirmPeriod({ preview, onCancel, onConfirm }) {
  const nm = preview?.moves.length || 0;
  const nc = preview?.cancels.length || 0;
  return (
    <AlertDialog open={!!preview} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="period-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Salvare i nuovi orari?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              <p>Periodo: <strong>{preview && periodLabel(preview.period)}</strong></p>
              {nm + nc === 0 && <p>Nessun appuntamento già preso viene modificato.</p>}
              {nm > 0 && (
                <div>
                  <p className="font-600 text-amber-700">{nm === 1 ? "1 appuntamento cambia" : `${nm} appuntamenti cambiano`} orario (stesso giorno, ISF avvisati):</p>
                  <ul className="max-h-28 list-disc overflow-y-auto pl-5 text-xs" data-testid="period-moves-list">
                    {preview.moves.map((m) => <li key={`${m.date}-${m.old}`}>{fmtShort(m.date)} · {m.old} → <b>{m.new}</b> · {m.isf}</li>)}
                  </ul>
                </div>
              )}
              {nc > 0 && (
                <div>
                  <p className="font-600 text-red-600">{nc === 1 ? "1 appuntamento verrà annullato" : `${nc} appuntamenti verranno annullati`} (nessun orario disponibile quel giorno):</p>
                  <ul className="max-h-28 list-disc overflow-y-auto pl-5 text-xs" data-testid="period-cancels-list">
                    {preview.cancels.map((c) => <li key={`${c.date}-${c.time}`}>{fmtShort(c.date)} · {c.time} · {c.isf}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="period-cancel-btn">Annulla</AlertDialogCancel>
          <AlertDialogAction className="bg-blue-700 hover:bg-blue-800" onClick={onConfirm} data-testid="period-confirm-btn">Salva orari</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PeriodList({ periods, onDelete }) {
  const sorted = useMemo(() => [...periods].sort((a, b) => a.start.localeCompare(b.start)), [periods]);
  if (!periods.length) return <p className="text-xs text-slate-400" data-testid="periods-empty">Nessun periodo programmato: valgono gli orari standard.</p>;
  return (
    <div className="space-y-1.5" data-testid="periods-list">
      {sorted.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs" data-testid={`period-${p.id}`}>
          <span><b className="text-slate-800">{periodLabel(p)}</b> · <span className="text-slate-500">{summary(p.weekly_slots)}</span></span>
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-red-600 hover:bg-red-50" onClick={() => onDelete(p.id)} data-testid={`delete-period-${p.id}`}>
            <Trash2 className="h-3.5 w-3.5" /> Elimina
          </Button>
        </div>
      ))}
    </div>
  );
}

export function SchedulePeriods({ profile, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState("range");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [slots, setSlots] = useState(profile.weekly_slots || {});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const valid = start && (mode === "open" || (end && end >= start));
  const body = () => ({ start, end: mode === "range" ? end : null, weekly_slots: slots });

  const ask = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/medico/periods/preview", body());
      setPreview({ ...data, period: body() });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/medico/periods", body());
      toast.success(`Orari del periodo salvati · ${data.moves.length} spostati · ${data.cancels.length} annullati`);
      setStart(""); setEnd("");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setPreview(null);
    setBusy(false);
  };
  const remove = async (id) => {
    try { await api.delete(`/medico/periods/${id}`); toast.success("Periodo eliminato: tornano gli orari standard"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const tab = (m, label) => (
    <button type="button" onClick={() => setMode(m)} data-testid={`period-mode-${m}`}
      className={`rounded-md px-3 py-1.5 text-xs font-600 transition-colors ${mode === m ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:text-slate-900"}`}>
      {label}
    </button>
  );

  return (
    <div className="rounded-xl border-2 border-blue-600 bg-white p-5" data-testid="schedule-periods">
      <h3 className="flex items-center gap-2 font-heading font-700 text-slate-900"><CalendarRange className="h-4 w-4 text-blue-700" /> Orari diversi per un periodo</h3>
      <p className="mb-3 text-xs text-slate-500">Scegli da quando cambiano gli orari e, se vuoi, fino a quando. Gli appuntamenti già presi restano nello stesso giorno e cambiano solo orario.</p>
      <div className="mb-3 flex flex-wrap gap-2">{tab("range", "Dal giorno … al giorno …")}{tab("open", "Dal giorno … in avanti")}</div>
      <div className="mb-4 flex flex-wrap gap-3">
        <label className="text-xs text-slate-500">Dal
          <Input type="date" min={today} value={start} onChange={(e) => { setStart(e.target.value); if (end && e.target.value > end) setEnd(e.target.value); }} className="mt-1 h-9 w-44" data-testid="period-start-input" />
        </label>
        {mode === "range" && (
          <label className="text-xs text-slate-500">Al
            <Input type="date" min={start || today} value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-9 w-44" data-testid="period-end-input" />
          </label>
        )}
      </div>
      <p className="mb-2 text-xs font-600 text-slate-700">Orari di ricevimento in questo periodo</p>
      <WeeklySlotsEditor value={slots} onChange={setSlots} />
      <Button onClick={ask} disabled={!valid || busy} className="mt-4 w-full gap-2 rounded-full bg-blue-700 hover:bg-blue-800" data-testid="period-save-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarRange className="h-4 w-4" />} Salva orari del periodo
      </Button>
      <p className="mb-1.5 mt-5 text-xs font-600 text-slate-700">Periodi programmati</p>
      <PeriodList periods={profile.schedule_periods || []} onDelete={remove} />
      <ConfirmPeriod preview={preview} onCancel={() => setPreview(null)} onConfirm={save} />
    </div>
  );
}
